import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '@xinfer/api-client';
import type { Account, ApiKey, MintedKey } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  ConfirmDialog,
  CopyButton,
  DataTable,
  PageHeader,
  RelativeTime,
  StatusBadge,
  toast,
  usePageTitle,
  type ColumnDef,
} from '@xinfer/ui';
import { Info, Plus } from 'lucide-react';
import { KeyRevealModal } from '../components/KeyRevealModal';

export function Keys({ account }: { account: Account }) {
  usePageTitle('API keys');
  const queryClient = useQueryClient();
  const [minted, setMinted] = useState<MintedKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const keys = useQuery({
    queryKey: ['keys', account.id],
    queryFn: () => client.listKeys(account.id),
  });

  const create = useMutation({
    mutationFn: () => client.createKey(account.id),
    onSuccess: (result) => {
      // Shown once, in a modal that cannot be dismissed by accident.
      setMinted(result);
      queryClient.invalidateQueries({ queryKey: ['keys', account.id] });
    },
    onError: () => toast.error('Could not create a key.'),
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => client.revokeKey(account.id, keyId),
    onSuccess: () => {
      setRevoking(null);
      toast.success('Key revoked. It reaches the serving path on the next policy poll.');
      queryClient.invalidateQueries({ queryKey: ['keys', account.id] });
    },
    onError: () => toast.error('Could not revoke that key.'),
  });

  const columns = useMemo<ColumnDef<ApiKey, unknown>[]>(
    () => [
      {
        accessorKey: 'key_id',
        header: 'Key',
        cell: ({ row }) => (
          // Identified by its public half and last four characters. The hash
          // the gate authenticates against is never sent here.
          <div className="flex items-center gap-1">
            <span className="font-mono">{row.original.key_id}</span>
            <span className="font-mono text-muted-foreground">…{row.original.hint}</span>
            <CopyButton value={row.original.key_id} />
          </div>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <RelativeTime value={row.original.created_at} className="text-muted-foreground" />
        ),
      },
      {
        accessorKey: 'last_used_at',
        header: 'Last used',
        cell: ({ row }) => (
          <RelativeTime value={row.original.last_used_at} className="text-muted-foreground" />
        ),
      },
      {
        accessorKey: 'revoked',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.revoked ? 'revoked' : 'active'} />,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.revoked ? null : (
            <div className="text-right">
              <Button variant="ghost" size="sm" onClick={() => setRevoking(row.original)}>
                Revoke
              </Button>
            </div>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        title="API keys"
        description="Used as the bearer token against the inference endpoint."
        actions={
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            <Plus />
            {create.isPending ? 'Creating…' : 'Create key'}
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={keys.data?.data}
        loading={keys.isLoading}
        error={keys.isError}
        errorMessage="Keys could not be listed."
        emptyTitle="No keys yet"
        emptyDescription="Create one to start making requests. It is shown once, at the moment it is created."
        getRowId={(row) => row.key_id}
      />

      <Alert className="mt-6">
        <Info />
        <div>
          <AlertTitle>Revocation is fast, not instant</AlertTitle>
          <AlertDescription>
            A revoked key stops working at the serving path within a second or so under normal
            conditions. The gate applies revocations from a signed policy snapshot it polls for,
            so if the control plane is unreachable it may take longer — and the gate keeps
            authenticating from the last snapshot it verified rather than failing open.
          </AlertDescription>
        </div>
      </Alert>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title="Revoke this key?"
        description={
          <>
            <span className="font-mono">{revoking?.key_id}</span> stops authenticating as soon as
            the gate applies the next policy snapshot. This cannot be undone; anything using it
            will start receiving <code>401</code>.
          </>
        }
        confirmLabel="Revoke key"
        destructive
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking.key_id)}
      />

      {minted && <KeyRevealModal minted={minted} onClose={() => setMinted(null)} />}
    </>
  );
}
